import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import Hero from './Hero';
import Benefits from './Benefits';
import VideoSection from './VideoSection';
import HowItWorks from './HowItWorks';
import Features from './Features';
import Testimonials from './Testimonials';
import UseCases from './UseCases';
import Pricing from './Pricing';
import FAQ from './FAQ';
import FinalCTA from './FinalCTA';
import Footer from './Footer';
import StickyOrderButton from './StickyOrderButton';
import SmartPopup from './SmartPopup';
import { trackCTAClick, trackOrderInitiated, trackLeadSubmission } from './AnalyticsIntegration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [showOrderModal, setShowOrderModal] = useState(false);

  const handleOrderClick = (location) => {
    trackCTAClick('Order Now', location);
    setShowOrderModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Announcement Bar */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-4 text-center font-semibold">
        <span className="text-sm md:text-base">
          🔥 Limited Time Offer: Get Your AIQR Kit at ₹1999 - Only 23 Kits Left! ⏰
        </span>
      </div>

      <Header onOrderClick={() => handleOrderClick('header')} />
      <Hero onOrderClick={() => handleOrderClick('hero')} />
      
      {/* Product Demo Video Section */}
      <VideoSection 
        title="See AIQR Kit In Action 🎥"
        subtitle="Watch how businesses are transforming walk-ins into WhatsApp leads in just 30 seconds"
        videoId="x7SaL4l-gOw"
        showCTA={true}
        onCTAClick={() => handleOrderClick('video-section')}
      />
      
      <Benefits />
      <HowItWorks />
      <Features />
      <Pricing onOrderClick={() => handleOrderClick('pricing')} />
      <Testimonials />
      <UseCases />
      <FAQ />
      <FinalCTA onOrderClick={() => handleOrderClick('final-cta')} />
      <Footer />
      <StickyOrderButton onClick={() => handleOrderClick('sticky-button')} />
      <SmartPopup />

      {/* Order Modal */}
      {showOrderModal && (
        <OrderModal onClose={() => setShowOrderModal(false)} />
      )}
    </div>
  );
};

const OrderModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    quantity: 1,
    business_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Track order initiation
    trackOrderInitiated(formData.quantity, formData.quantity * 1999);
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/order-enquiry`, formData);
      
      // Track lead submission
      trackLeadSubmission('order_form');
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Order AIQR Kit</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Order Received!</h3>
            <p className="text-gray-600">We'll contact you shortly to confirm your order.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5, 10].map(num => (
                    <option key={num} value={num}>{num} Kit{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  value={formData.business_type}
                  onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                >
                  <option value="">Select...</option>
                  <option value="retail">Retail Store</option>
                  <option value="salon">Salon/Spa</option>
                  <option value="restaurant">Restaurant/Cafe</option>
                  <option value="clinic">Clinic/Hospital</option>
                  <option value="gym">Gym/Fitness</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                <textarea
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Any special requirements?"
                ></textarea>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50"
              data-testid="order-submit-button"
            >
              {loading ? 'Submitting...' : `Order Now - ₹${formData.quantity * 1999}`}
            </button>
            <p className="text-xs text-gray-500 text-center mt-3">We'll contact you to confirm and process payment</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LandingPage;